import React, { useContext, useMemo, useState } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  HubOption,
  HubOptionStateValue,
  HUB_MAX_REPLAY_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import type { ReplayDefaults, ReplayCondition } from 'dorian-shared/types/branch/HubOption';
import {
  MemoryDTO,
  MemoryType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { NodeEditContext } from '../../../../context/NodeEditContext';
import { OptionSectionRow } from '../../shared/OptionSectionRow';
import { BranchSelectItem } from '../../shared/types';
import { getBranchName } from '../../shared/hubUtils';
import { useReplayConditions } from '../../hooks/useReplayConditions';
import { ReplayDefaultControls } from './ReplayDefaultControls';
import { ReplayConditionCard } from './ReplayConditionCard';
import { isReplayConditionValid } from './replayConditionValidation';
import './HubOptionReplay.scss';

export type HubOptionReplayProps = {
  optionUuid: string;
  replay: ReplayDefaults | null;
  replayConditions: ReplayCondition[];
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  onChange: (changes: Partial<HubOption>) => void;
};

/** Stable sensor config — defined at module level to avoid re-creation on every render */
const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

export function HubOptionReplay({
  optionUuid,
  replay,
  replayConditions,
  branches,
  currentBranchId,
  isDisabled,
  onChange,
}: HubOptionReplayProps) {
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const memoryBankSlots = (ctx?.memoryBankSlots as MemoryDTO[] | undefined) ?? [];

  const numberVariables = useMemo(
    () => memoryBankSlots.filter((v: MemoryDTO) => v.type === MemoryType.Number),
    [memoryBankSlots],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
    summary: conditionSummary,
  } = useReplayConditions(replayConditions, onChange, numberVariables);

  const [isOverridesExpanded, setIsOverridesExpanded] = useState(false);

  const isEnabled = replay != null && replay.state !== HubOptionStateValue.Hidden;
  const replayGotoBranchId = replay?.gotoBranchId ?? null;
  const replayText = replay?.text ?? 'Replay';
  const hasOverrides = replayConditions.length > 0;

  const isConfigured = useMemo(() => hasOverrides && replayConditions.every((c) => {
    const subsValid = c.subConditions.length > 0 &&
      c.subConditions.every((sub) => {
        if (sub.variableId == null) return false;
        const hasInt = sub.intValue !== undefined && sub.intValue !== '';
        const hasVar = sub.varValue !== undefined && sub.varValue !== null && sub.varValue !== 0;
        return hasInt || hasVar;
      });
    if (!subsValid) return false;

    if (c.resultState == null) return false;
    if (c.resultState === HubOptionStateValue.Hidden) return true;
    if (c.resultState === HubOptionStateValue.Disabled) return true;
    return c.resultGotoBranchId != null;
  }), [hasOverrides, replayConditions]);

  const canAdd = replayConditions.length < HUB_MAX_REPLAY_CONDITIONS;

  const sortableItems = useMemo(
    () => replayConditions.map((c) => c.uuid),
    [replayConditions],
  );

  const summaryText = useMemo((): string | undefined => {
    if (hasOverrides && conditionSummary) return conditionSummary;
    if (!isEnabled) return undefined;
    const destName = getBranchName(replayGotoBranchId, branches);
    if (replayText && destName) return `↺ ${replayText} → ${destName}`;
    if (destName) return `↺ → ${destName}`;
    if (replayText) return `↺ ${replayText}`;
    return undefined;
  }, [isEnabled, replayGotoBranchId, replayText, hasOverrides, conditionSummary, branches]);

  return (
    <OptionSectionRow
      label="↺ Replay"
      inlineControl={(
        <ReplayDefaultControls
          replay={replay}
          branches={branches}
          currentBranchId={currentBranchId}
          isDisabled={isDisabled}
          onChange={onChange}
        />
      )}
      summary={summaryText}
      isConfigured={isConfigured}
      showDot={hasOverrides}
      hasError={hasOverrides && !isConfigured}
      expandLabel="Overrides"
      isExpanded={isOverridesExpanded}
      onExpandToggle={() => setIsOverridesExpanded((prev) => !prev)}
    >
      <div className="hub-option-replay" data-testid="hub-option-replay">
        <p className="hub-option-replay__helper">
          First match overrides replay state, icon, text, and destination. Evaluated top-to-bottom.
        </p>

        <DndContext
          id={`replay-${optionUuid}`}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            {replayConditions.map((cond) => (
              <ReplayConditionCard
                key={cond.uuid}
                condition={cond}
                memoryVariables={numberVariables}
                branches={branches}
                currentBranchId={currentBranchId}
                isDisabled={isDisabled}
                isValid={isReplayConditionValid(cond)}
                onChange={(updated) => updateCondition(cond.uuid, updated)}
                onRemove={() => removeCondition(cond.uuid)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Otherwise row — pinned last, not editable */}
        <div className="hub-option-replay__otherwise" data-testid="replay-otherwise-row">
          <span className="hub-option-replay__otherwise-label">Otherwise</span>
          <span className="hub-option-replay__otherwise-text">
            uses defaults above
          </span>
        </div>

        {/* Add button + counter */}
        <div className="hub-option-replay__footer">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={addCondition}
            disabled={isDisabled || !canAdd}
            data-testid="add-replay-condition"
          >
            + Add Replay Override
          </button>
          <span className="hub-option-replay__counter" data-testid="replay-condition-counter">
            {replayConditions.length} / {HUB_MAX_REPLAY_CONDITIONS}
          </span>
        </div>
      </div>
    </OptionSectionRow>
  );
}
